import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Box, ArrowRightLeft, Trash2, MessageCircle, Settings, DollarSign, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function Boxes({ user }) {
  const [boxes, setBoxes] = useState([]);
  const [openRegisters, setOpenRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [expensesWhatsapp, setExpensesWhatsapp] = useState("");
  const [boxSettings, setBoxSettings] = useState({ uses_dollars: true, uses_banks: true, assigned_bank_id: "" });
  
  const [banks, setBanks] = useState([]);
  
  const [newBoxData, setNewBoxData] = useState({
    name: "",
    cajero_username: "",
    cajero_password: "",
    uses_dollars: true,
    uses_banks: true
  });
  
  const [transferData, setTransferData] = useState({
    from_box_id: "",
    to_box_id: "",
    amount_colones: 0,
    amount_dollars: 0,
    description: ""
  });

  const fetchBoxes = async () => {
    try {
      const response = await axiosInstance.get('/boxes');
      setBoxes(response.data);
    } catch (error) {
      console.error("Error fetching boxes:", error);
    }
  };

  const fetchOpenRegisters = async () => {
    try {
      if (user.role === 'master') {
        const response = await axiosInstance.get('/cash-register/all-open');
        setOpenRegisters(response.data);
      }
    } catch (error) {
      console.error("Error fetching open registers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoxes();
    fetchOpenRegisters();
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await axiosInstance.get('/banks');
      setBanks(response.data || []);
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  };

  const handleCreateBox = async () => {
    try {
      await axiosInstance.post('/boxes', newBoxData);
      toast.success("Caja y cajero creados exitosamente");
      setShowCreateDialog(false);
      setNewBoxData({ name: "", cajero_username: "", cajero_password: "" });
      fetchBoxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear caja");
    }
  };

  const handleDeleteBox = async () => {
    try {
      await axiosInstance.delete(`/boxes/${selectedBox.id}?password=${deletePassword}`);
      toast.success("Caja eliminada exitosamente");
      setShowDeleteDialog(false);
      setSelectedBox(null);
      setDeletePassword("");
      fetchBoxes();
      fetchOpenRegisters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar caja");
    }
  };

  const openDeleteDialog = (box) => {
    setSelectedBox(box);
    setShowDeleteDialog(true);
  };

  const handleTransfer = async () => {
    try {
      await axiosInstance.post('/transfers', transferData);
      toast.success("Transferencia realizada exitosamente");
      setShowTransferDialog(false);
      setTransferData({
        from_box_id: "",
        to_box_id: "",
        amount_colones: 0,
        amount_dollars: 0,
        description: ""
      });
      fetchOpenRegisters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al realizar transferencia");
    }
  };

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };

  const getBoxRegister = (boxId) => {
    return openRegisters.find(r => r.box_id === boxId);
  };

  const openWhatsAppDialog = (box) => {
    setSelectedBox(box);
    setWhatsappNumber(box.whatsapp_number || "");
    setExpensesWhatsapp(box.expenses_alert_whatsapp || "");
    setShowWhatsAppDialog(true);
  };

  const openSettingsDialog = (box) => {
    setSelectedBox(box);
    const em = box.enabled_modules || {};
    setBoxSettings({
      uses_dollars: box.uses_dollars !== false,
      uses_banks: box.uses_banks !== false,
      assigned_bank_id: box.assigned_bank_id || "",
      acts_as_master: !!box.acts_as_master,
      // Online store
      store_enabled: !!box.store_enabled,
      store_slug: box.store_slug || "",
      store_title: box.store_title || "",
      store_description: box.store_description || "",
      store_whatsapp: box.store_whatsapp || "",
      store_hours_text: box.store_hours_text || "",
      store_open_time: box.store_open_time || "",
      store_close_time: box.store_close_time || "",
      store_open_days: box.store_open_days || [0, 1, 2, 3, 4, 5],
      store_logo_url: box.store_logo_url || "",
      store_theme_color: box.store_theme_color || "#0F172A",
      store_accent_color: box.store_accent_color || "#10B981",
      store_hero_message: box.store_hero_message || "",
      store_background_url: box.store_background_url || "",
      modules: {
        inventory: em.inventory !== false,
        layaways: em.layaways !== false,
        sinpe: em.sinpe !== false,
        sublimation: em.sublimation !== false,
        arts: em.arts !== false,
      },
    });
    setShowSettingsDialog(true);
  };

  const handleUpdateSettings = async () => {
    try {
      let url = `/boxes/${selectedBox.id}/settings?uses_dollars=${boxSettings.uses_dollars}&uses_banks=${boxSettings.uses_banks}`;
      if (boxSettings.assigned_bank_id !== undefined) {
        url += `&assigned_bank_id=${boxSettings.assigned_bank_id || ""}`;
      }
      await axiosInstance.patch(url);
      // Save module toggles
      if (boxSettings.modules) {
        await axiosInstance.patch(`/boxes/${selectedBox.id}/modules`, { modules: boxSettings.modules });
      }
      // Save acts_as_master flag
      await axiosInstance.patch(`/boxes/${selectedBox.id}/acts-as-master`, { acts_as_master: !!boxSettings.acts_as_master });
      // Save online store settings
      await axiosInstance.patch(`/boxes/${selectedBox.id}/store-settings`, {
        store_enabled: !!boxSettings.store_enabled,
        store_slug: boxSettings.store_slug || "",
        store_title: boxSettings.store_title || "",
        store_description: boxSettings.store_description || "",
        store_whatsapp: boxSettings.store_whatsapp || "",
        store_hours_text: boxSettings.store_hours_text || "",
        store_open_time: boxSettings.store_open_time || "",
        store_close_time: boxSettings.store_close_time || "",
        store_open_days: boxSettings.store_open_days || [],
        store_logo_url: boxSettings.store_logo_url || "",
        store_theme_color: boxSettings.store_theme_color || "",
        store_accent_color: boxSettings.store_accent_color || "",
        store_hero_message: boxSettings.store_hero_message || "",
        store_background_url: boxSettings.store_background_url || "",
      });
      toast.success("Configuración actualizada");
      setShowSettingsDialog(false);
      setSelectedBox(null);
      fetchBoxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar configuración");
    }
  };

  const handleUpdateWhatsApp = async () => {
    try {
      await axiosInstance.patch(`/boxes/${selectedBox.id}/whatsapp?whatsapp_number=${encodeURIComponent(whatsappNumber)}`);
      await axiosInstance.patch(`/boxes/${selectedBox.id}/expenses-whatsapp?whatsapp_number=${encodeURIComponent(expensesWhatsapp)}`);
      toast.success("Números de WhatsApp actualizados");
      setShowWhatsAppDialog(false);
      setSelectedBox(null);
      setWhatsappNumber("");
      setExpensesWhatsapp("");
      fetchBoxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar WhatsApp");
    }
  };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  if (user.role !== 'master') {
    return (
      <div className="text-center py-20">
        <p className="text-slate-600">Solo el Master puede gestionar las cajas</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Gestión de Cajas
          </h1>
          <p className="text-slate-600">Administra múltiples cajas y transferencias</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            data-testid="transfer-button"
            onClick={() => setShowTransferDialog(true)}
            className="bg-purple-600 hover:bg-purple-700 gap-2"
            disabled={openRegisters.length < 2}
          >
            <ArrowRightLeft className="w-4 h-4" />
            Transferir
          </Button>
          <Button
            data-testid="create-box-button"
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Plus className="w-4 h-4" />
            Nueva Caja
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {boxes.map((box) => {
          const register = getBoxRegister(box.id);
          const isOpen = !!register;
          
          return (
            <Card
              key={box.id}
              data-testid={`box-${box.id}`}
              className={`bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg hover:shadow-xl transition-shadow ${
                isOpen ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-300'
              }`}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2 flex-1">
                    <Box className={`w-5 h-5 ${isOpen ? 'text-green-600' : 'text-gray-400'}`} />
                    {box.name}
                    {/* Show settings indicators */}
                    <div className="flex items-center gap-1 ml-2">
                      {box.uses_dollars === false && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded" title="No usa dólares">
                          Sin $
                        </span>
                      )}
                      {box.uses_banks === false && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded" title="No usa BANCO_SINPE">
                          Sin Bancos
                        </span>
                      )}
                      {box.assigned_bank_name && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded" title={`Banco: ${box.assigned_bank_name}`}>
                          {box.assigned_bank_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isOpen ? 'Abierta' : 'Cerrada'}
                    </span>
                    <Button
                      data-testid={`settings-box-${box.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => openSettingsDialog(box)}
                      className="hover:bg-blue-50 hover:text-blue-600 h-8 w-8"
                      title="Configuración de caja"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`whatsapp-box-${box.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => openWhatsAppDialog(box)}
                      className="hover:bg-green-50 hover:text-green-600 h-8 w-8"
                      title="Configurar WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    {!isOpen && (
                      <Button
                        data-testid={`delete-box-${box.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(box)}
                        className="hover:bg-red-50 hover:text-red-600 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isOpen ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                        <span className="text-sm text-slate-600">Efectivo Colones:</span>
                        <span className="font-semibold text-blue-700">
                          {formatCurrency(register.total_cash_colones)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                        <span className="text-sm text-slate-600">Efectivo Dólares:</span>
                        <span className="font-semibold text-green-700">
                          {formatCurrency(register.total_cash_dollars, "dollars")}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Abierta por: {register.opened_by}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No hay caja abierta
                  </p>
                )}
                <p className="text-xs text-slate-400">
                  Creada: {new Date(box.created_at).toLocaleDateString('es-CR')}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Box Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-box-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Caja</DialogTitle>
            <DialogDescription>Crea una nueva caja con su cajero asignado</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="box_name">Nombre de la Caja</Label>
              <Input
                id="box_name"
                data-testid="box-name-input"
                type="text"
                value={newBoxData.name}
                onChange={(e) => setNewBoxData({...newBoxData, name: e.target.value})}
                className="mt-1.5"
                placeholder="Ej: Caja 1, Caja Principal, etc."
              />
            </div>

            <div className="pt-2 border-t">
              <p className="text-sm font-semibold text-slate-700 mb-3">Datos del Cajero</p>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="cajero_username">Nombre de Usuario del Cajero</Label>
                  <Input
                    id="cajero_username"
                    data-testid="cajero-username-input"
                    type="text"
                    value={newBoxData.cajero_username}
                    onChange={(e) => setNewBoxData({...newBoxData, cajero_username: e.target.value})}
                    className="mt-1.5"
                    placeholder="Ej: Juan_Caja1, Maria_Norte, etc."
                  />
                </div>

                <div>
                  <Label htmlFor="cajero_password">Contraseña del Cajero</Label>
                  <Input
                    id="cajero_password"
                    data-testid="cajero-password-input"
                    type="text"
                    value={newBoxData.cajero_password}
                    onChange={(e) => setNewBoxData({...newBoxData, cajero_password: e.target.value})}
                    className="mt-1.5"
                    placeholder="Asigna una contraseña"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setNewBoxData({ name: "", cajero_username: "", cajero_password: "" });
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-create-box-button"
              onClick={handleCreateBox}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!newBoxData.name || !newBoxData.cajero_username || !newBoxData.cajero_password}
            >
              Crear Caja y Cajero
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>Configurar WhatsApp</DialogTitle>
            <DialogDescription>
              Asigna un número de WhatsApp para la caja "{selectedBox?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="whatsapp_number">Número de WhatsApp (soporte)</Label>
            <Input
              id="whatsapp_number"
              data-testid="whatsapp-number-input"
              type="text"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              className="mt-1.5"
              placeholder="Ej: 50612345678"
            />
            <p className="text-xs text-slate-500 mt-1">
              Formato: código de país + número (sin espacios ni guiones)
            </p>
          </div>

          <div className="border-t pt-4">
            <Label htmlFor="expenses_whatsapp" className="flex items-center gap-1">
              <span>📱 WhatsApp para alertas de gastos</span>
            </Label>
            <Input
              id="expenses_whatsapp"
              data-testid="expenses-whatsapp-input"
              type="text"
              value={expensesWhatsapp}
              onChange={(e) => setExpensesWhatsapp(e.target.value)}
              className="mt-1.5"
              placeholder="Ej: 50688887777"
            />
            <p className="text-xs text-amber-700 mt-1 bg-amber-50 p-2 rounded">
              💡 Este número recibirá el aviso desde Gastos cuando un gasto esté a <strong>1 día de vencer</strong> o <strong>vencido</strong>. Deja vacío para desactivar.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowWhatsAppDialog(false);
              setWhatsappNumber("");
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-whatsapp-button"
              onClick={handleUpdateWhatsApp}
              className="bg-green-600 hover:bg-green-700"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Box Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-box-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar Caja</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar la caja "{selectedBox?.name}"? Esta acción eliminará también su cajero asociado y todos los registros históricos. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="delete_password">Contraseña Master</Label>
            <Input
              id="delete_password"
              data-testid="delete-box-password-input"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="mt-1.5"
              placeholder="Ingresa la contraseña master"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeletePassword("");
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-box-button"
              onClick={handleDeleteBox}
              variant="destructive"
              disabled={!deletePassword}
            >
              Eliminar Caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transferencia entre Cajas</DialogTitle>
            <DialogDescription>Transfiere dinero de una caja a otra</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="from_box">Caja Origen</Label>
              <Select
                value={transferData.from_box_id}
                onValueChange={(value) => setTransferData({...transferData, from_box_id: value})}
              >
                <SelectTrigger data-testid="from-box-select" className="mt-1.5">
                  <SelectValue placeholder="Selecciona caja origen" />
                </SelectTrigger>
                <SelectContent>
                  {openRegisters.map(register => (
                    <SelectItem key={register.box_id} value={register.box_id}>
                      {register.box_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="to_box">Caja Destino</Label>
              <Select
                value={transferData.to_box_id}
                onValueChange={(value) => setTransferData({...transferData, to_box_id: value})}
              >
                <SelectTrigger data-testid="to-box-select" className="mt-1.5">
                  <SelectValue placeholder="Selecciona caja destino" />
                </SelectTrigger>
                <SelectContent>
                  {openRegisters
                    .filter(r => r.box_id !== transferData.from_box_id)
                    .map(register => (
                      <SelectItem key={register.box_id} value={register.box_id}>
                        {register.box_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount_colones">Monto en Colones</Label>
              <Input
                id="amount_colones"
                data-testid="transfer-colones-input"
                type="number"
                step="0.01"
                value={transferData.amount_colones}
                onChange={(e) => setTransferData({...transferData, amount_colones: parseFloat(e.target.value) || 0})}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="amount_dollars">Monto en Dólares</Label>
              <Input
                id="amount_dollars"
                data-testid="transfer-dollars-input"
                type="number"
                step="0.01"
                value={transferData.amount_dollars}
                onChange={(e) => setTransferData({...transferData, amount_dollars: parseFloat(e.target.value) || 0})}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                data-testid="transfer-description-input"
                type="text"
                value={transferData.description}
                onChange={(e) => setTransferData({...transferData, description: e.target.value})}
                className="mt-1.5"
                placeholder="Motivo de la transferencia"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-transfer-button"
              onClick={handleTransfer}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={!transferData.from_box_id || !transferData.to_box_id || !transferData.description}
            >
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent data-testid="settings-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración de Caja
            </DialogTitle>
            <DialogDescription>
              Configura las opciones de {selectedBox?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium">Usa Dólares</p>
                  <p className="text-xs text-slate-500">
                    Si está desactivado, los montos en dólares serán ignorados en Excel
                  </p>
                </div>
              </div>
              <Switch
                checked={boxSettings.uses_dollars}
                onCheckedChange={(checked) => setBoxSettings({...boxSettings, uses_dollars: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-medium">Usa Bancos (PAGO_SINPE)</p>
                  <p className="text-xs text-slate-500">
                    Si está desactivado, no se podrá usar PAGO_SINPE en Excel. ENTRADA_BANCO y SALIDA_BANCO siguen funcionando.
                  </p>
                </div>
              </div>
              <Switch
                checked={boxSettings.uses_banks}
                onCheckedChange={(checked) => setBoxSettings({...boxSettings, uses_banks: checked})}
              />
            </div>

            <div className="p-3 border rounded-lg md:col-span-2">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-medium">Banco Asignado</p>
                  <p className="text-xs text-slate-500">
                    Banco que usará esta caja para ENTRADA_BANCO y SALIDA_BANCO
                  </p>
                </div>
              </div>
              <Select 
                value={boxSettings.assigned_bank_id || "none"} 
                onValueChange={(val) => setBoxSettings({...boxSettings, assigned_bank_id: val === "none" ? "" : val})}
              >
                <SelectTrigger data-testid="bank-select">
                  <SelectValue placeholder="Seleccionar banco..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin banco asignado</SelectItem>
                  {banks.map(bank => (
                    <SelectItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional Modules toggles */}
            <div className="p-3 border rounded-lg bg-slate-50 md:col-span-2">
              <p className="font-medium text-slate-800 mb-1">Módulos disponibles</p>
              <p className="text-xs text-slate-500 mb-3">
                Activa solo los módulos que esta caja usará. Los demás (Dashboard, Movimientos, Reportes, Cierre, Bancos, Clientes, Préstamos) siempre están visibles.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {[
                  { key: "inventory", label: "Inventario", hint: "Productos, ventas, ajustes físicos" },
                  { key: "layaways", label: "Apartados", hint: "Reservas con abonos" },
                  { key: "sinpe", label: "SINPE Verifier", hint: "Verificación IMAP de transferencias" },
                  { key: "labels", label: "Etiquetas", hint: "Generador e impresión de etiquetas" },
                  { key: "sublimation", label: "Sublimación", hint: "Módulo (próximamente)" },
                  { key: "arts", label: "Artes", hint: "Módulo (próximamente)" },
                ].map((m) => (
                  <div key={m.key} className="flex items-center justify-between gap-2 py-1" data-testid={`module-toggle-${m.key}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{m.label}</p>
                      <p className="text-[11px] text-slate-500 truncate">{m.hint}</p>
                    </div>
                    <Switch
                      checked={boxSettings.modules?.[m.key] !== false}
                      onCheckedChange={(checked) =>
                        setBoxSettings({
                          ...boxSettings,
                          modules: { ...(boxSettings.modules || {}), [m.key]: checked },
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Acts as MASTER toggle */}
            <div className="p-3 border rounded-lg border-amber-200 bg-amber-50 md:col-span-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-amber-800">Trabaja como MASTER</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Cuando está activo, los cajeros de esta caja pueden aplicar descuentos en ventas <b>sin solicitar aprobación</b> al MASTER.
                  </p>
                </div>
                <Switch
                  checked={!!boxSettings.acts_as_master}
                  onCheckedChange={(checked) => setBoxSettings({ ...boxSettings, acts_as_master: checked })}
                  data-testid="acts-as-master-toggle"
                />
              </div>
            </div>

            {/* Online Store */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-sm text-slate-700 mb-3">🛒 Tienda en línea</h3>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Label className="font-semibold">Activar tienda pública</Label>
                    <p className="text-xs text-emerald-700 mt-1">
                      Permite que cualquiera vea el catálogo de esta caja en <code>/tienda/SLUG</code>.
                    </p>
                  </div>
                  <Switch
                    checked={!!boxSettings.store_enabled}
                    onCheckedChange={(c) => setBoxSettings({ ...boxSettings, store_enabled: c })}
                    data-testid="store-enabled-toggle"
                  />
                </div>
                {boxSettings.store_enabled && (
                  <>
                    <div>
                      <Label>Slug (URL pública)</Label>
                      <Input
                        data-testid="store-slug-input"
                        placeholder="caja-tucan"
                        value={boxSettings.store_slug || ""}
                        onChange={(e) => setBoxSettings({ ...boxSettings, store_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                      />
                      {boxSettings.store_slug && (
                        <div className="text-xs text-emerald-700 mt-1 break-all">
                          URL pública: <code>{window.location.origin}/tienda/{boxSettings.store_slug}</code>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Título de la tienda</Label>
                      <Input
                        placeholder="Mi Tienda"
                        value={boxSettings.store_title || ""}
                        onChange={(e) => setBoxSettings({ ...boxSettings, store_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Descripción corta</Label>
                      <Input
                        placeholder="La mejor tienda de..."
                        value={boxSettings.store_description || ""}
                        onChange={(e) => setBoxSettings({ ...boxSettings, store_description: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>WhatsApp para recibir pedidos</Label>
                      <Input
                        placeholder="50688881111"
                        value={boxSettings.store_whatsapp || ""}
                        onChange={(e) => setBoxSettings({ ...boxSettings, store_whatsapp: e.target.value.replace(/\D/g, "") })}
                      />
                    </div>

                    {/* Schedule */}
                    <div className="rounded-md border border-emerald-300 bg-white p-2 space-y-2">
                      <div className="text-xs font-bold text-emerald-700">🕐 Horario de atención</div>
                      <Input
                        placeholder="Lun-Vie 9am-6pm, Sáb 9am-1pm"
                        value={boxSettings.store_hours_text || ""}
                        onChange={(e) => setBoxSettings({ ...boxSettings, store_hours_text: e.target.value })}
                        data-testid="store-hours-text"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Abre</Label>
                          <Input type="time" value={boxSettings.store_open_time || ""} onChange={(e) => setBoxSettings({ ...boxSettings, store_open_time: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Cierra</Label>
                          <Input type="time" value={boxSettings.store_close_time || ""} onChange={(e) => setBoxSettings({ ...boxSettings, store_close_time: e.target.value })} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Días abiertos</Label>
                        <div className="flex gap-1 flex-wrap mt-1">
                          {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((d, idx) => {
                            const days = boxSettings.store_open_days || [];
                            const sel = days.includes(idx);
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  const next = sel ? days.filter((x) => x !== idx) : [...days, idx];
                                  setBoxSettings({ ...boxSettings, store_open_days: next.sort() });
                                }}
                                className={`px-2 py-1 text-xs rounded border ${sel ? "bg-emerald-500 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`}
                              >
                                {d}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Branding */}
                    <div className="rounded-md border border-emerald-300 bg-white p-2 space-y-2">
                      <div className="text-xs font-bold text-emerald-700">🎨 Personalización visual</div>
                      <div>
                        <Label className="text-xs">Logo (URL)</Label>
                        <Input placeholder="https://..." value={boxSettings.store_logo_url || ""} onChange={(e) => setBoxSettings({ ...boxSettings, store_logo_url: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Imagen de fondo (URL, opcional)</Label>
                        <Input placeholder="https://..." value={boxSettings.store_background_url || ""} onChange={(e) => setBoxSettings({ ...boxSettings, store_background_url: e.target.value })} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Color principal (header)</Label>
                          <div className="flex gap-1">
                            <input type="color" value={boxSettings.store_theme_color || "#0F172A"} onChange={(e) => setBoxSettings({ ...boxSettings, store_theme_color: e.target.value })} className="h-9 w-12 border rounded cursor-pointer" />
                            <Input value={boxSettings.store_theme_color || "#0F172A"} onChange={(e) => setBoxSettings({ ...boxSettings, store_theme_color: e.target.value })} />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Color acento (precio/botón)</Label>
                          <div className="flex gap-1">
                            <input type="color" value={boxSettings.store_accent_color || "#10B981"} onChange={(e) => setBoxSettings({ ...boxSettings, store_accent_color: e.target.value })} className="h-9 w-12 border rounded cursor-pointer" />
                            <Input value={boxSettings.store_accent_color || "#10B981"} onChange={(e) => setBoxSettings({ ...boxSettings, store_accent_color: e.target.value })} />
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Mensaje destacado (banner promo)</Label>
                        <Input placeholder="🎉 20% en camisas este mes" value={boxSettings.store_hero_message || ""} onChange={(e) => setBoxSettings({ ...boxSettings, store_hero_message: e.target.value })} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="save-settings-button"
              onClick={handleUpdateSettings}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}